// Standard Library
// 3rd Party
// Local

/**
 * SVG Icon Set Element
 * Stores and manages SVG icon definitions
 * Icons are defined as SVG elements with unique IDs
 */
class SvgIconSetElement extends HTMLElement {
  /**
   * Gets the list of attributes to observe for changes
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return ['name', 'size'];
  }

  /**
   * Sets the icon set name
   * @param {string} newName - The new name for the icon set
   */
  set name(newName) {
    this.setAttribute('name', newName);
  }

  /**
   * Gets the icon set name
   * @returns {string} The current icon set name
   */
  get name() {
    return this._name;
  }

  /**
   * Sets the default icon size
   * @param {number} newSize - The new default size for icons
   */
  set size(newSize) {
    this.setAttribute('size', newSize);
  }

  /**
   * Gets the default icon size
   * @returns {number} The current default icon size
   */
  get size() {
    return this._size;
  }

  /**
   * Creates a new SVG icon set element
   */
  constructor() {
    super();
    this._size = 24;
  }

  /**
   * Called when the element is connected to the DOM
   * Hides the icon set element from display
   */
  connectedCallback() {
    this.style.display = 'none';
  }

  /**
   * Handles attribute changes
   * @param {string} name - The name of the changed attribute
   * @param {string} oldValue - The previous value of the attribute
   * @param {string} newValue - The new value of the attribute
   */
  attributeChangedCallback(name, oldValue, newValue) {
    this[`_${name}`] = newValue;
  }

  /**
   * Creates a map of all icons in this set
   * @returns {object} Map of icon IDs to icon elements
   */
  _createIconMap() {
    const icons = {};
    this.querySelectorAll(`[id]`).forEach((icon) => {
      icons[icon.id] = icon;
    });
    return icons;
  }

  /**
   * Gets all icon names in this set
   * @returns {string[]} Array of icon names prefixed with the set name
   */
  getIconNames() {
    this._icons = this._createIconMap();
    return Object.keys(this._icons).map((iconName) => {
      return this._name + ':' + iconName;
    }, this);
  }

  /**
   * Applies an icon to an element
   * @param {HTMLElement} element - The element to apply the icon to
   * @param {string} iconName - The name of the icon to apply
   * @returns {SVGElement|null} The applied SVG element or null if not found
   */
  applyIcon(element, iconName) {
    this.removeIcon(element);
    const svg = this._cloneIcon(iconName);

    if (svg) {
      const pde = element.shadowRoot || element;
      pde.insertBefore(svg, pde.childNodes[0]);
      return (element._svgIcon = svg);
    }
    return null;
  }

  /**
   * Removes an icon from an element
   * @param {HTMLElement} element - The element to remove the icon from
   */
  removeIcon(element) {
    if (element._svgIcon) {
      const pde = element.shadowRoot || element;
      pde.removeChild(element._svgIcon);
      element._svgIcon = null;
    }
  }

  /**
   * Clones an icon by ID
   * @param {string} id - The ID of the icon to clone
   * @returns {SVGElement|null} The cloned SVG element or null if not found
   */
  _cloneIcon(id) {
    this._icons = this._icons || this._createIconMap();
    return this._prepareSvgClone(this._icons[id], this._size);
  }

  /**
   * Prepares a cloned SVG element with proper attributes
   * @param {SVGElement} sourceSvg - The source SVG element to clone
   * @param {number} size - The size for the cloned SVG
   * @returns {SVGElement|null} The prepared SVG clone or null if source is invalid
   */
  _prepareSvgClone(sourceSvg, size) {
    if (sourceSvg) {
      const content = sourceSvg.cloneNode(true);
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const viewBox = content.getAttribute('viewBox') || `0 0 ${size} ${size}`;
      const cssText = `
        pointer-events: none; 
        display: block; 
        width: 100%; 
        height: 100%;
      `;

      svg.setAttribute('viewBox', viewBox);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('focusable', 'false');
      svg.style.cssText = cssText;
      svg.appendChild(content).removeAttribute('id');

      return svg;
    }
    return null;
  }
}

export { SvgIconSetElement };
